import { debounce } from 'lodash';
import { analyticsAPI } from './AnalyticsAPI';
import type { PageChangeEvent, PageViewEvent, TrackingPayload } from '@publicala/delfino';

const DEBOUNCE_DELAY_MS = 1000;

/**
 * Extract the trailing run of digits from a pagebreak id. Handles both our
 * own injected `pla-pb-N` prefix and publisher-emitted variants like `p23`,
 * `page_42`, or `chapter1-pb-3` (last digit run wins, so the inner `1` from
 * `chapter1` is correctly ignored). Returns `null` when the id has no digits.
 */
function extractNumericId(id: string): number | null {
    const matches = id.match(/\d+/g);
    if (!matches || matches.length === 0) return null;
    return Number(matches[matches.length - 1]);
}

/**
 * Vue event bus interface for listening to page change events.
 * This is specific to Volpe's Vue implementation.
 */
interface EventBus {
    $on(event: 'pageChanged', callback: (e: PageChangeEvent) => void): void;
    $on(event: 'pageView', callback: (e: PageViewEvent) => void): void;
    $on(event: string, callback: (...args: unknown[]) => void): void;
}

/**
 * Listens to user events and sends them to analytics.
 * Events: page-change, page-view, tab-hidden, tab-visible
 */
class TrackingEventsListener {
    private eventBus: EventBus | null = null;
    private isInitialized = false;

    /**
     * Initializes the listener with an event bus and registers all event handlers.
     * @param eventBus - Vue event bus for listening to page changes
     */
    init(eventBus: EventBus): void {
        if (this.isInitialized) return;

        this.eventBus = eventBus;
        this.registerEventListeners();
        this.isInitialized = true;
    }

    /**
     * Registers all event listeners for page changes and visibility changes.
     */
    private registerEventListeners(): void {
        if (!this.eventBus) return;

        this.eventBus.$on(
            'pageChanged',
            debounce(
                (event: PageChangeEvent) => {
                    this.trackPageChange(event);
                },
                DEBOUNCE_DELAY_MS,
                { leading: true, trailing: true }
            )
        );

        this.eventBus.$on(
            'pageView',
            debounce(
                (event: PageViewEvent) => {
                    this.trackPageView(event);
                },
                DEBOUNCE_DELAY_MS,
                { leading: true, trailing: true }
            )
        );

        document.addEventListener(
            'visibilitychange',
            () => {
                if (document.hidden) {
                    this.trackTabHidden();
                } else {
                    this.trackTabVisible();
                }
            },
            false
        );
    }

    /**
     * Tracks a page change event.
     * @param event - Page change data (pageNumber for PDF, extraPayload.epub for EPUB)
     */
    private trackPageChange(event: PageChangeEvent): void {
        const payload: TrackingPayload = {
            currentPage: event.pageNumber ?? null,
            extraPayload: event.extraPayload,
        };

        analyticsAPI.track('page-change', payload);
    }

    /**
     * Tracks a confirmed page view (printed page visible for the configured
     * delay in EPUB content, or inferred page-view in fixed-layout / PDF).
     *
     * For reflowable EPUBs that emit a real pagebreak element, the raw `id`
     * and parsed `numericId` ride along in `extraPayload.pagebreak` so the
     * host can distinguish publisher-printed pages from inferred page indices
     * without parsing prefixes.
     */
    private trackPageView(event: PageViewEvent): void {
        const payload: TrackingPayload = {
            currentPage: event.title ? Number(event.title) : null,
            extraPayload: event.id
                ? {
                      pagebreak: {
                          id: event.id,
                          numericId: extractNumericId(event.id),
                          title: event.title,
                      },
                  }
                : undefined,
        };

        analyticsAPI.track('page-view', payload);
    }

    /**
     * Tracks when user switches away from the tab.
     */
    private trackTabHidden(): void {
        analyticsAPI.track('tab-hidden', {});
    }

    /**
     * Tracks when user returns to the tab.
     */
    private trackTabVisible(): void {
        analyticsAPI.track('tab-visible', {});
    }
}

export const trackingEventsListener = new TrackingEventsListener();
